// src/a11y/AccessibilityModal.tsx
import React from 'react'
import { useLanguage } from '../context/LanguageContext'
import { a11yLabels } from '../locales/translations'

import iconVoice from '../assets/Voice.png'
import iconMono from '../assets/BlackWhite.png'
import iconText from '../assets/Text.png'
import iconLine from '../assets/Height.png'
import iconSpace from '../assets/Space.png'
import iconCursor from '../assets/Cursor.png'
import accessibilityIcon from '../assets/Spec.svg'

type Lang = 'ru' | 'en' | 'kz'

type Props = {
  open: boolean
  onClose?: () => void
  /** можно передать снаружи, чтобы совпадало с твоим layout lang={lang} */
  lang?: Lang
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))

function A11yIcon({ src, alt }: { src: string; alt: string }) {
  return <img src={src} alt={alt} className="a11y-tile-icon-img" draggable={false} />
}

function tr(obj: any, lang: Lang, fallback: Lang = 'ru'): string {
  if (!obj || typeof obj !== 'object') return ''
  return obj[lang] ?? obj[fallback] ?? ''
}

function applyRoot({
  mono,
  cursor,
  font,
  lineHeight,
  letterSpacing
}: {
  mono: boolean
  cursor: boolean
  font: number
  lineHeight: number
  letterSpacing: number
}) {
  const root = document.documentElement
  root.classList.toggle('a11y-mono', !!mono)
  root.classList.toggle('a11y-big-cursor', !!cursor)

  root.style.setProperty('--a11y-font', String(font))
  root.style.setProperty('--a11y-line', String(lineHeight))
  root.style.setProperty('--a11y-letter', `${letterSpacing}em`)
}

export default function AccessibilityModal({ open, onClose, lang: langProp }: Props) {
  const { language } = useLanguage()
  const lang: Lang = (langProp ?? (language as Lang) ?? 'ru')

  const [mono, setMono] = React.useState<boolean>(() => localStorage.getItem('a11yMono') === '1')
  const [cursor, setCursor] = React.useState<boolean>(() => localStorage.getItem('a11yCursor') === '1')
  const [tts, setTts] = React.useState<boolean>(() => localStorage.getItem('a11yTts') === '1')

  const [font, setFont] = React.useState<number>(() => {
    const v = Number(localStorage.getItem('a11yFont') || '1')
    return Number.isFinite(v) ? v : 1
  })

  const [lineHeight, setLineHeight] = React.useState<number>(() => {
    const v = Number(localStorage.getItem('a11yLine') || '1.3')
    return Number.isFinite(v) ? v : 1.3
  })

  const [letterSpacing, setLetterSpacing] = React.useState<number>(() => {
    const v = Number(localStorage.getItem('a11yLetter') || '0')
    return Number.isFinite(v) ? v : 0
  })

  React.useEffect(() => {
    localStorage.setItem('a11yMono', mono ? '1' : '0')
    localStorage.setItem('a11yCursor', cursor ? '1' : '0')
    localStorage.setItem('a11yFont', String(font))
    localStorage.setItem('a11yLine', String(lineHeight))
    localStorage.setItem('a11yLetter', String(letterSpacing))
    localStorage.setItem('a11yTts', tts ? '1' : '0')

    applyRoot({ mono, cursor, font, lineHeight, letterSpacing })
  }, [mono, cursor, font, lineHeight, letterSpacing, tts])

  // ESC закрывает
  React.useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // блокируем скролл фона, пока модалка открыта
  React.useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  const reset = () => {
    setMono(false)
    setCursor(false)
    setFont(1)
    setLineHeight(1.3)
    setLetterSpacing(0)
    setTts(false)
  }

  if (!open) return null

  return (
    <div
      className="a11y-modal a11y-ignore-tts"
      role="dialog"
      aria-modal="true"
      aria-label={tr(a11yLabels.title, lang)}
    >
      <div className="a11y-modal-backdrop" onClick={onClose} />

      <div className="a11y-modal-panel">
        <div className="a11y-modal-head">
          <div className="a11y-page-head" style={{ paddingTop: 0 }}>
            <h2 className="a11y-page-title">{tr(a11yLabels.title, lang)}</h2>
          </div>

          <button
            type="button"
            className="a11y-modal-close"
            onClick={onClose}
            aria-label={tr(a11yLabels.aria.close, lang)}
          >
            ✕
          </button>
        </div>

        <div className="a11y-page-card">
          <div className="a11y-page-row">
            <div className="a11y-page-row-left">
              <img src={accessibilityIcon} alt="" className="sidebar-link-icon" aria-hidden="true" />
              <span className="a11y-page-row-text">{tr(a11yLabels.profiles, lang)}</span>
            </div>
            <span className="a11y-page-row-hint">ⓘ</span>
          </div>

          <div className="a11y-tiles">
            {/* Озвучка */}
            <button
              type="button"
              className={'a11y-tile' + (tts ? ' on' : '')}
              onClick={() => setTts((v) => !v)}
              aria-pressed={tts}
            >
              <div className="a11y-tile-icon">
                <A11yIcon src={iconVoice} alt="Voice" />
              </div>
              <div className="a11y-tile-title">{tr(a11yLabels.tiles.tts, lang)}</div>
              <div className="a11y-tile-foot">{tts ? tr(a11yLabels.on, lang) : tr(a11yLabels.off, lang)}</div>
            </button>

            {/* Ч/Б */}
            <button
              type="button"
              className={'a11y-tile' + (mono ? ' on' : '')}
              onClick={() => setMono((v) => !v)}
              aria-pressed={mono}
            >
              <div className="a11y-tile-icon">
                <A11yIcon src={iconMono} alt="Black & White" />
              </div>
              <div className="a11y-tile-title">{tr(a11yLabels.tiles.mono, lang)}</div>
              <div className="a11y-tile-foot">{mono ? tr(a11yLabels.on, lang) : tr(a11yLabels.off, lang)}</div>
            </button>

            {/* Текст */}
            <div className={'a11y-tile' + (font !== 1 ? ' on' : '')}>
              <div className="a11y-tile-icon">
                <A11yIcon src={iconText} alt="Text size" />
              </div>
              <div className="a11y-tile-title">{tr(a11yLabels.tiles.textSize, lang)}</div>
              <div className="a11y-step">
                <button
                  type="button"
                  onClick={() => setFont((f) => clamp(Math.round((f - 0.1) * 10) / 10, 0.9, 1.4))}
                  aria-label={tr(a11yLabels.aria.decreaseText, lang)}
                >
                  −
                </button>
                <span>{Math.round(font * 100)}%</span>
                <button
                  type="button"
                  onClick={() => setFont((f) => clamp(Math.round((f + 0.1) * 10) / 10, 0.9, 1.4))}
                  aria-label={tr(a11yLabels.aria.increaseText, lang)}
                >
                  +
                </button>
              </div>
            </div>

            {/* Межстрочный */}
            <div className={'a11y-tile' + (lineHeight !== 1.3 ? ' on' : '')}>
              <div className="a11y-tile-icon">
                <A11yIcon src={iconLine} alt="Line height" />
              </div>
              <div className="a11y-tile-title">{tr(a11yLabels.tiles.lineHeight, lang)}</div>
              <div className="a11y-step">
                <button
                  type="button"
                  onClick={() => setLineHeight((v) => clamp(Math.round((v - 0.1) * 10) / 10, 1.1, 2.0))}
                  aria-label={tr(a11yLabels.aria.decreaseText, lang)}
                >
                  −
                </button>
                <span>×{lineHeight.toFixed(1)}</span>
                <button
                  type="button"
                  onClick={() => setLineHeight((v) => clamp(Math.round((v + 0.1) * 10) / 10, 1.1, 2.0))}
                  aria-label={tr(a11yLabels.aria.increaseText, lang)}
                >
                  +
                </button>
              </div>
            </div>

            {/* Буквы */}
            <div className={'a11y-tile' + (letterSpacing !== 0 ? ' on' : '')}>
              <div className="a11y-tile-icon">
                <A11yIcon src={iconSpace} alt="Letter spacing" />
              </div>
              <div className="a11y-tile-title">{tr(a11yLabels.tiles.letterSpacing, lang)}</div>
              <div className="a11y-step">
                <button
                  type="button"
                  onClick={() => setLetterSpacing((v) => clamp(Math.round((v - 0.02) * 100) / 100, 0, 0.2))}
                  aria-label={tr(a11yLabels.aria.decreaseText, lang)}
                >
                  −
                </button>
                <span>{letterSpacing.toFixed(2)}em</span>
                <button
                  type="button"
                  onClick={() => setLetterSpacing((v) => clamp(Math.round((v + 0.02) * 100) / 100, 0, 0.2))}
                  aria-label={tr(a11yLabels.aria.increaseText, lang)}
                >
                  +
                </button>
              </div>
            </div>

            {/* Курсор */}
            <button
              type="button"
              className={'a11y-tile' + (cursor ? ' on' : '')}
              onClick={() => setCursor((v) => !v)}
              aria-pressed={cursor}
            >
              <div className="a11y-tile-icon">
                <A11yIcon src={iconCursor} alt="Cursor" />
              </div>
              <div className="a11y-tile-title">{tr(a11yLabels.tiles.cursor, lang)}</div>
              <div className="a11y-tile-foot">{cursor ? tr(a11yLabels.on, lang) : tr(a11yLabels.off, lang)}</div>
            </button>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <button type="button" className="a11y-reset" onClick={reset}>
              {tr(a11yLabels.reset, lang)}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
